import { FC, ReactElement, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { Alert, Button, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { PatientTherapyBillingInfoDto } from '../../../../shared/dtos/patient.dto';
import { TherapyBillStatus } from '../../../../shared/utils/enums';
import { TherapyBillingCreateRequest } from '../../../../shared/requests/therapyBillingCreate.request';
import { TherapyBillingUpdateRequest } from '../../../../shared/requests/therapyBillingUpdate.request';

type FormValues = {
  unitAmount: number;
  billingStatus?: TherapyBillStatus | null;
}

type PatientBillingFormProps = {
  patientId: string;
  data?: PatientTherapyBillingInfoDto
  onClose: () => void;
  onSubmit: () => void;
}

const PatientBillingForm: FC<PatientBillingFormProps> = ({
  patientId,
  data,
  onClose,
  onSubmit
}: PatientBillingFormProps): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = {
    unitAmount: data?.unitAmount ?? 0,
    billingStatus: data?.status ?? null,
  };
  
  const isEdit: boolean = !!data;

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    unitAmount: Yup.number().integer().moreThan(0).required(i18next.t('generic.required')),
    billingStatus: isEdit 
      ? Yup.mixed<TherapyBillStatus>().oneOf(Object.values(TherapyBillStatus)).required(i18next.t('generic.required')) 
      : Yup.mixed<TherapyBillStatus>().oneOf(Object.values(TherapyBillStatus)).nullable()
  });
  
  const handleCreate = async (values: FormValues): Promise<void> => {
    const request: TherapyBillingCreateRequest = {
      patientId: patientId,
      unitAmount: values.unitAmount,
    };
    
    await authFetch('/api/v1/therapy-billing/', { method: 'POST', body: request });
  };
  
  const handleEdit = async (values: FormValues): Promise<void> => {
    const request: TherapyBillingUpdateRequest = {
      unitAmount: values.unitAmount,
      status: values.billingStatus!
    };
    
    await authFetch(`/api/v1/therapy-billing/${data!.id}`, { method: 'PATCH', body: request });
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);
    

    try {
      if (isEdit) {
        await handleEdit(values);
      } else {
        await handleCreate(values);
      }
      
      onSubmit();
    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, setFieldValue, touched, errors, handleSubmit, handleChange, handleBlur, isSubmitting }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1 }}>
            <Stack gap={3}>
              <Row>
                <Form.Group controlId="unitAmount">
                  <Form.Label>
                    {i18next.t('patientBillingModal.unitAmount')}
                  </Form.Label>
                  <Form.Control
                    name="unitAmount"
                    type="number"
                    value={values.unitAmount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.unitAmount && !!errors.unitAmount}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.unitAmount}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              
              {isEdit && (
                <Row>
                  <Form.Group controlId="billingStatus">
                    <Form.Label>
                      {i18next.t('patientBillingModal.billingStatus')}
                    </Form.Label>
                    <Form.Select
                      name="billingStatus"
                      className="select-xl"
                      value={values.billingStatus ?? ''}
                      onChange={(event) => setFieldValue('billingStatus', event.target.value as TherapyBillStatus)}
                      onBlur={handleBlur}
                      isInvalid={touched.billingStatus && !!errors.billingStatus}
                    >
                      {Object.values(TherapyBillStatus).map((status) => (
                        <option
                          key={status}
                          value={status}>
                          {i18next.t('patientBillingModal.therapyBillingStatus.' + status)}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.billingStatus}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              )}
              
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.close')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.save')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PatientBillingForm;
